import type { PriceDetailsQueryParams } from '@/scopes/common/infrastructure/price-details/api/types'
import { Icon, Modal, SpaceBetween, tk, Typography } from '@sunwebgroup/ui-components'
import { useState } from 'react'
import { styled } from 'styled-components'
import { useIntl } from '../../../intl'
import { usePriceDetails, usePriceDetailsSelect } from '../domain/price-details/hooks'

interface PriceDetailsProps {
  priceDetailsQueryParams: PriceDetailsQueryParams
}

interface PriceDetailsResultProps {
  onClose: () => void
  defaultTitle: string
}

interface PriceDetailsInfoProps {
  defaultTitle: string
  openModal: () => void
  priceDetailsQueryParams: PriceDetailsQueryParams
}

const StyledButton = styled.button`
  position: relative;
  height: ${tk('font_size_md')};
  margin-top: ${tk('spacing_xs')};
  color: ${tk('color_semantic_icon_strong')};
`

const StyledErrorMessage = styled(SpaceBetween)`
  text-align: center;
  margin: 0 ${tk('spacing_md')};
  color: ${tk('color_semantic_text_strong')};

  @media ${tk('mq_tablet')} {
    margin: 0 ${tk('spacing_5xl')};
    max-height: calc(
      100vh - 120px
    ); // Set max height with viewport height minus modal header (56px) and spacing_4xl (64px) from Modal parent element, so content is not scrollable on smaller screens.
    height: 684px; // Set height with the modal's height (740px) from design minus modal header (56px).
  }
`

const StyledPriceDetails = styled(Typography)`
  color: ${tk('color_semantic_text')};
  padding: ${tk('spacing_md')};

  > * table {
    margin-bottom: ${tk('spacing_sm')};
  }

  > * p {
    margin-top: ${tk('spacing_sm')};
    margin-bottom: 0;
  }

  > * b + p {
    margin-top: ${tk('spacing_2xs')};
  }

  > * a {
    color: ${tk('color_semantic_text')};
  }

  // only apply margin-top to <b> elements that are not nested inside a <p> element
  > * b:not(p b) {
    display: inline-block;
    margin-top: ${tk('spacing_sm')};
  }

  @media ${tk('mq_tablet')} {
    > * table {
      width: 100%;
    }

    > * td {
      width: 50%;
    }
  }
`

function PriceDetailsResult({ onClose, defaultTitle }: PriceDetailsResultProps) {
  const intl = useIntl()
  const { isLoading, error, priceDetails } = usePriceDetails()

  return (
    <Modal.Root open onClose={onClose}>
      <Modal.Content $size="md">
        <Modal.Header>
          <Modal.HeaderTitle text={priceDetails?.title ?? defaultTitle} />
          <Modal.HeaderCloseButton />
        </Modal.Header>
        <Modal.Body>
          {isLoading && <Modal.Spinner />}
          {error && (
            <StyledErrorMessage direction="y" spacing={tk('spacing_sm')} alignItems="center" justifyContent="center">
              <Icon glyph="IconWarningTriangle" size="md" />
              <Typography as="p" $variant="body-lg-bold">
                {intl.formatMessage({ id: 'price_details_error_msg' })}
              </Typography>
            </StyledErrorMessage>
          )}
          {/* TODO: Remove dangerouslySetInnerHTML when new price breakdown design is implemented */}
          {priceDetails && (
            <StyledPriceDetails
              as="div"
              $variant="body-md"
              dangerouslySetInnerHTML={{ __html: priceDetails.content }}
            />
          )}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  )
}

export function PriceDetailsInfo({ defaultTitle, openModal, priceDetailsQueryParams }: PriceDetailsInfoProps) {
  const { setPriceDetailsQueryParams } = usePriceDetailsSelect()

  const handleOnClick = () => {
    openModal()
    setPriceDetailsQueryParams(priceDetailsQueryParams)
  }

  return (
    <StyledButton onClick={handleOnClick} aria-label={defaultTitle}>
      <Icon glyph="IconInformationCircle" size="sm" />
    </StyledButton>
  )
}

export function PriceDetails({ priceDetailsQueryParams }: PriceDetailsProps) {
  const intl = useIntl()
  const [showModal, setShowModal] = useState(false)
  const defaultTitle = intl.formatMessage({ id: 'price_overview' })

  const onClose = () => {
    setShowModal(false)
  }

  const openModal = () => {
    setShowModal(true)
  }

  return (
    <>
      <PriceDetailsInfo
        openModal={openModal}
        priceDetailsQueryParams={priceDetailsQueryParams}
        defaultTitle={defaultTitle}
      />
      {showModal && <PriceDetailsResult onClose={onClose} defaultTitle={defaultTitle} />}
    </>
  )
}
